import { Modal } from "./Modal"
import { RelaySetCard } from "./RelaySetCard"
import { RelayIcon } from "./Avatar"
import { Icon } from "./Icon"
import { useAuth } from "@/state/AuthContext"
import { useFeed } from "@/state/FeedContext"
import { useRelays } from "@/state/RelayContext"
import { simplifyUrl } from "@/utils/url"
import { Link } from "react-router-dom"

export interface FeedSwitcherProps {
  open: boolean
  onClose: () => void
}

/**
 * The relay feed switcher: "Following" plus every relay set and single relay
 * the user has added. Selecting one replaces the active feed everywhere.
 */
export function FeedSwitcher({ open, onClose }: FeedSwitcherProps) {
  const { pubkey } = useAuth()
  const { relaySets, favoriteRelays } = useRelays()
  const { feedInfo, switchToFollowing, switchToRelaySet, switchToRelay } = useFeed()

  return (
    <Modal open={open} onClose={onClose} title="Switch feed" description="Pick a relay set or a single relay to read." size="small">
      <div className="feed-switcher">
        <section>
          <h3 className="section-label">Personal feeds</h3>
          <button
            type="button"
            className={`feed-option ${feedInfo?.feedType === "following" ? "active" : ""}`}
            disabled={!pubkey}
            onClick={() => {
              switchToFollowing()
              onClose()
            }}
          >
            <span className="feed-option-icon">
              <Icon name="users" size={16} />
            </span>
            <span className="feed-option-name">Following</span>
            {!pubkey && <span className="badge">log in</span>}
          </button>
        </section>

        <section>
          <div className="section-head">
            <h3 className="section-label">Relay feeds</h3>
            <Link to="/settings#relays" className="link-small" onClick={onClose}>
              edit
            </Link>
          </div>
          {relaySets.map((set) => (
            <RelaySetCard
              key={set.id}
              set={set}
              active={feedInfo?.feedType === "relays" && feedInfo.id === set.id}
              onSelect={() => {
                switchToRelaySet(set.id)
                onClose()
              }}
            />
          ))}
          {favoriteRelays.map((url) => (
            <button
              key={url}
              type="button"
              className={`feed-option ${feedInfo?.feedType === "relay" && feedInfo.id === url ? "active" : ""}`}
              onClick={() => {
                switchToRelay(url)
                onClose()
              }}
            >
              <RelayIcon url={url} size={20} />
              <span className="feed-option-name">{simplifyUrl(url)}</span>
            </button>
          ))}
          {relaySets.length === 0 && favoriteRelays.length === 0 && (
            <p className="muted small">Add relays in Settings to build relay feeds.</p>
          )}
        </section>
      </div>
    </Modal>
  )
}
